import * as ko from "knockout";
import {
    IButton,
    IKnockoutComponent,
    IRouteDefinition,
    IRouteDefinitionParams,
    TBaseOrObservableType,
    TRouteId
} from "../../index";
import {getRouteDefinition} from "../../constants/RouteDefinitions";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {_} from 'lodash';
import {nsRoute} from "../../util/route";
import {Tab} from "./Tab";
import {TComponentName} from "../../constants/ComponentRegistry";
import {global} from "../../appContext";
import {_d} from "../../util/misc";
import {ButtonManager} from "../../util/ButtonManager";
import {
    getTabMemoryObservable,
    resolveShellTabId,
} from "../../util/pageShellTabMemory";
import {EMPTY_HEADER_ACTIONS, IPageShellHeaderActions} from '../page-shell/pageShellHeaderActions';
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;

export class TabManager extends ParsedRouteDataDecorator {
    readonly isPageShell: boolean = false;
    /** UI selection (may differ from persisted memory on availability fallback). */
    public currentId: KnockoutObservable<TRouteId | null>;
    /** Persisted last-tab preference for this shell's memory key. */
    protected readonly tabMemory: KnockoutObservable<TRouteId | null>;
    private _tabs: Tab[];
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    currentComponent: KnockoutObservable<IKnockoutComponent | null> = ko.observable(null);
    buttonManager: ButtonManager = new ButtonManager()
    /**
     * Form/content shells can publish tab-row actions (e.g. preview while editing) that stay
     * visible across tabs. Cleared when the content viewModel disposes.
     */
    readonly contentHeaderActions: KnockoutObservable<IPageShellHeaderActions> = ko.observable(EMPTY_HEADER_ACTIONS);
    /**
     * Optional veto for tab clicks (e.g. unsaved edit-permissions changes).
     * Return false to abort; content may clear the interceptor and re-run onClick to proceed.
     */
    public tabClickInterceptor: ((tabId: TRouteId) => boolean) | null = null;

    /**
     * Form content-wrappers keep inactive route-destination panels mounted; tab-panels unload.
     * Public so route-destination need not read protected __routeDef.
     */
    public shouldKeepDestinationPanelsAlive(): boolean {
        const content = this.__routeDef.content;
        return typeof content === 'string' && content !== 'component/page-shell/tab-panels';
    }

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.tabMemory = getTabMemoryObservable(this.__routeDef);
        this.currentId = ko.observable<TRouteId | null>(null);
        this._tabs = (this.__routeDef.tabs ?? [])
            .map(tab => getRouteDefinition(tab))
            .filter((def): def is IRouteDefinition => def != null)
            .map((def: IRouteDefinition) => new Tab(this, def));
        const initialId = this.resolveInitialTabId();
        if (initialId) {
            this.currentId(initialId);
            if (!this.tabMemory()) {
                this.tabMemory(initialId);
            }
        }
        this.kosub.subscribe(this._tabs.map(o => o.isVisible), this.updateTabsOnVisibilityChange.bind(this))
        this.kosub.subscribe(this.currentId, () => this.updateComponent());
        if (!this.isPageShell) {
            this.updateComponent();
        }
        if (this.__requestRouteData) {
            this.__requestRouteData!.request()
                .catch((reason: string) => _d.error(reason));
        }
    }

    protected _reqUpdateTabsOnVisibilityChange!: number;

    /** When the current tab is hidden, show the first presented tab without clobbering memory. */
    protected updateTabsOnVisibilityChange() {
        cancelAnimationFrame(this._reqUpdateTabsOnVisibilityChange);
        this._reqUpdateTabsOnVisibilityChange = requestAnimationFrame(() => {
            const current = this.currentTab();
            if (current && this.isTabAvailable(current)) {
                return;
            }
            const tab = this._tabs.find(t => this.isTabAvailable(t));
            if (tab?.id) {
                this.selectTabWithoutPersisting(tab.id);
            }
        });
    }

    /** Resolve cold-start / stored tab using shared adjudication rules. */
    protected resolveInitialTabId(): TRouteId | null {
        return resolveShellTabId(this.__routeDef, {
            storedTabId: this.tabMemory(),
            isAvailable: (tabId) => {
                const tab = this.getTab(tabId);
                return !!tab && this.isTabAvailable(tab);
            },
        });
    }

    public tabs = ko.pureComputed(() => {
        return this._tabs.filter(t => this.isTabAvailable(t));
    });

    public currentTab = ko.pureComputed((): Tab | null => {
        if (!this.currentId()) {
            return null;
        }
        return this._tabs.find(o => o.id == this.currentId()) ?? null;
    })

    private currentDef = ko.pureComputed((): IRouteDefinition | null => {
        const def = this.getDef(this.currentId());
        return def ? getRouteDefinition(def) : null;
    })

    public getTab(id: TRouteId, optionalParams?: any): Tab | undefined {
        const tab = this._tabs.find(t => t.id == id);
        if (!tab) {
            return undefined;
        }
        if (optionalParams != null && Object.keys(optionalParams).length > 0) {
            return _.defaultsDeep(optionalParams, tab);
        }
        return tab;
    }

    public getBadge(id: TRouteId): Tab['badge'] | undefined {
        return this.getTab(id)?.badge;
    }

    public getDef(id: TRouteId | null | undefined): IRouteDefinition | undefined {
        if (!id) {
            return undefined;
        }
        return _.cloneDeep(this._tabs.find(t => t.id == id)?.__routeDef);
    }

    protected isTabAvailable(tab: Tab): boolean {
        if (tab.__routeDef.canSeeOwnPage) {
            if (global.login.user()?.id == this.__id) {
                return true;
            }
        }
        return tab.isVisible();
    }

    protected updateComponent() {
        let component: IKnockoutComponent;
        let def: IRouteDefinition;
        if ('content' in this.__routeDef) {
            const routeDef = getRouteDefinition(this.__routeDef);
            if (!routeDef) {
                return;
            }
            def = routeDef;
            let name: TComponentName;
            if (this.__routeDef.content === true) {
                name = 'component/route/details/basic';
            } else {
                name = this.__routeDef.content as TComponentName;
            }
            component = {name: name};
        } else {
            const currentDef = this.currentDef();
            if (!currentDef) {
                return;
            }
            def = currentDef;
            component = _.clone(def.component as IKnockoutComponent);
            if ('__routeDef' in component) {
                delete component.params.__routeDef;
            }
        }
        component.params = _.defaults({
            __routeDef: def,
            __routeId: def.id,
            __requestRouteData: this.__requestRouteData,
            __routeParams: this.__routeParams,
            __tab: this.currentTab(),
            __tabManager: this,
            __id: this.__id,
        }, component.params);
        if (component.name == this.currentComponent?.()?.name) {
            return;
        }
        this.currentComponent(component as IKnockoutComponent);
    }

    private _reqScrollActiveContentToTop!: number;

    /** User-driven tab selection — updates UI and persisted memory. */
    public onClick(id: TRouteId) {
        if (this.tabClickInterceptor && !this.tabClickInterceptor(id)) {
            return;
        }
        this.selectTabAndPersist(id);
    }

    /** Select tab and remember it. */
    public selectTabAndPersist(id: TRouteId) {
        const tab = this.getTab(id);
        if (!tab || !this.isTabAvailable(tab)) {
            return;
        }
        this.currentId(id);
        this.tabMemory(id);
    }

    /** Select tab for display only (availability fallback — keep store preference). */
    public selectTabWithoutPersisting(id: TRouteId) {
        const tab = this.getTab(id);
        if (!tab) {
            return;
        }
        this.currentId(id);
    }

    public getAllTabs(): Tab[] {
        return this._tabs;
    }

    public addButton(btn: TBaseOrObservableType<IButton>, tab?: TRouteId) {
        if (tab) {
            this.tabs().find(t => t.id == tab)?.addButton(btn);
            return;
        }
        this.buttonManager.add(btn);
    }

    public getTabContentComponent(id: TRouteId) {
        return this.getTab(id)?.getContentComponent();
    }

    public getDestinations(): Array<{__routeId: TRouteId; component: IKnockoutComponent}> {
        const shellService = this.__routeDef.fetchRouteDef?.serviceName;
        return this.getAllTabs()
            .map((tab) => {
                const tabDef = getRouteDefinition(tab.id);
                const name = tabDef?.component?.name;
                if (!name || !tabDef) {
                    return null;
                }
                // Tabs with a different fetch (e.g. hierarchy) must not reuse the shell getobject.
                const tabService = tabDef.fetchRouteDef?.serviceName;
                const shareShellRequest = !tabService || tabService === shellService;
                const component: IKnockoutComponent = {
                    name,
                    params: {
                        __routeDef: this.getDef(tab.id) ?? tabDef,
                        __routeId: tab.id,
                        ...(shareShellRequest && this.__requestRouteData
                            ? {__requestRouteData: this.__requestRouteData}
                            : {}),
                        __routeParams: this.__routeParams,
                        __tab: tab,
                        __tabManager: this,
                        __id: this.__id,
                        ...(tabDef.component?.params ?? {}),
                    },
                };
                return {__routeId: tab.id, component};
            })
            .filter((p): p is {__routeId: TRouteId; component: IKnockoutComponent} => p != null);
    }


    validate(): boolean {
        return this.tabs().reduce((bool: boolean, tab: Tab) => tab.errorManager.validate() && bool, true)
    }

    /** Select tab by id (URL / soft-nav). Persists when the tab is available. */
    setTabId(tabId: TRouteId) {
        const tab = this.getTab(tabId);
        if (!tab) {
            return;
        }
        if (this.isTabAvailable(tab)) {
            this.selectTabAndPersist(tabId);
        } else {
            this.selectTabWithoutPersisting(tabId);
        }
    }

    /**
     * After object-driven show() updates, ensure the current tab is still presented;
     * subclasses (PageController) may also rewrite the URL.
     */
    public ensureCurrentTabAvailable(): TRouteId | null {
        const current = this.currentTab();
        if (current && this.isTabAvailable(current)) {
            return current.id;
        }
        const fallback = this._tabs.find(t => this.isTabAvailable(t));
        if (!fallback) {
            return null;
        }
        this.selectTabWithoutPersisting(fallback.id);
        return fallback.id;
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        this.tabs.dispose();
        this.currentTab.dispose();
        this.currentDef.dispose();
        cancelAnimationFrame(this._reqUpdateTabsOnVisibilityChange);
        cancelAnimationFrame(this._reqScrollActiveContentToTop);
    }

}
